import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const [step, setStep] = useState("mobile"); // mobile | otp
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [password, setPassword] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (mobile.length < 10) { toast.error("Enter 10-digit mobile"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/request-otp", { mobile });
      setStep("otp");
      if (r.data.debug_otp) {
        setDebugOtp(r.data.debug_otp);
        toast.warning("WhatsApp delivery failed — using DEV OTP shown below.");
      } else {
        toast.success("OTP sent on WhatsApp");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) { toast.error("Enter OTP"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { mobile, code: otp });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const passwordLogin = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/password-login", { mobile, password });
      login(r.data.token, r.data.user);
      toast.success(`Welcome, ${r.data.user.name}`);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-slate-900 grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 overflow-hidden">
        <div className="absolute inset-0 cpl-glow" />
        <div className="absolute inset-0" style={{
          backgroundImage: "url('https://images.pexels.com/photos/13216333/pexels-photo-13216333.jpeg')",
          backgroundSize: "cover", backgroundPosition: "center", opacity: 0.35,
        }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFFFFF] via-[#FFFFFF]/70 to-transparent" />
        <div className="relative">
          <Logo size="lg" />
        </div>
        <div className="relative">
          <h2 className="text-4xl tracking-tighter font-medium leading-tight">
            The Chiripal<br />
            <span className="text-[#E63946]">PPF Operations</span> console.
          </h2>
          <p className="text-[#475569] mt-4 max-w-md">Sign in with your registered mobile number — we'll send a WhatsApp OTP.</p>
        </div>
        <div className="relative text-xs text-[#64748B]">© Chiripal Poly Films Ltd.</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col p-6 lg:p-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-slate-900 mb-8" data-testid="back-to-home">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[11px] uppercase tracking-[0.18em] text-[#E63946] w-fit mb-4">
            <ShieldCheck size={12} /> Secure login
          </div>
          <h1 className="text-3xl lg:text-4xl tracking-tighter font-medium">
            {step === "mobile" ? "Sign in" : "Verify it's you"}
          </h1>
          <p className="text-[#475569] mt-2 text-sm">
            {step === "mobile"
              ? "Enter your registered mobile to receive a WhatsApp OTP."
              : `Enter the 6-digit code we just sent to +91 ${mobile}`}
          </p>

          {!showPwd && step === "mobile" && (
            <form onSubmit={sendOtp} className="mt-8 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Mobile Number</label>
                <div className="mt-1 flex">
                  <span className="px-3 py-3 bg-[#FFFFFF] border border-r-0 border-[#E5E7EB] rounded-l-md text-[#475569] font-mono text-sm">+91</span>
                  <input
                    data-testid="login-mobile-input"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="9999999999"
                    className="flex-1 px-3 py-3 bg-[#FFFFFF] border border-[#E5E7EB] rounded-r-md text-slate-900 font-mono focus:outline-none focus:border-[#E63946]"
                  />
                </div>
              </div>
              <button
                data-testid="send-otp-btn"
                type="submit" disabled={loading}
                className="w-full py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send WhatsApp OTP"}
              </button>
              <button
                type="button" onClick={() => setShowPwd(true)}
                data-testid="use-password-btn"
                className="w-full text-xs text-[#475569] hover:text-slate-900 flex items-center justify-center gap-2"
              >
                <KeyRound size={12} /> Use password instead (admin only)
              </button>
            </form>
          )}

          {showPwd && (
            <form onSubmit={passwordLogin} className="mt-8 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Mobile</label>
                <input
                  data-testid="login-pwd-mobile"
                  value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  placeholder="9999999999"
                  className="mt-1 w-full px-3 py-3 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-slate-900 focus:outline-none focus:border-[#E63946]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Password</label>
                <input
                  data-testid="login-pwd-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  type="password" placeholder="••••••••"
                  className="mt-1 w-full px-3 py-3 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
                />
              </div>
              <button
                data-testid="password-login-btn"
                type="submit" disabled={loading}
                className="w-full py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium disabled:opacity-60"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
              <button type="button" onClick={() => setShowPwd(false)} className="w-full text-xs text-[#475569] hover:text-slate-900">
                Use mobile OTP instead
              </button>
            </form>
          )}

          {step === "otp" && !showPwd && (
            <form onSubmit={verifyOtp} className="mt-8 space-y-4">
              {debugOtp && (
                <div className="px-3 py-2 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#FCA311] text-xs" data-testid="debug-otp-banner">
                  Dev OTP (WhatsApp not configured): <span className="font-mono font-semibold">{debugOtp}</span>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">6-digit OTP</label>
                <input
                  data-testid="otp-input"
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="mt-1 w-full px-3 py-3 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-2xl tracking-[0.5em] text-center text-slate-900 focus:outline-none focus:border-[#E63946]"
                  autoFocus
                />
              </div>
              <button
                data-testid="verify-otp-btn"
                type="submit" disabled={loading}
                className="w-full py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Verify & Sign in"}
              </button>
              <button type="button" onClick={() => { setStep("mobile"); setOtp(""); setDebugOtp(null); }} className="w-full text-xs text-[#475569] hover:text-slate-900">
                Change mobile number
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-[#E5E7EB] text-sm text-[#475569]">
            New distributor?{" "}
            <Link to="/register-distributor" className="text-[#E63946] hover:underline" data-testid="register-distributor-link">
              Register here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
